<!--
  The folder's actions, at the top of the CONTENT pane, beside its name.

  TASK-042 §3a. The order within the set is fixed by the requirement, quoted
  here verbatim so that nobody re-derives it from a mockup:

    "On the most right-hand side, Delete, then Rename on the most left-hand
     side of that set of options, and then the middle New Folder."

  So, left to right: RENAME, NEW FOLDER, DELETE. Delete is furthest right and
  is the only one styled as destructive. A test asserts this DOM order
  positionally, because a fixed order that is only written down in prose is an
  order that survives until the first refactor.

  SHARE SITS TO THE LEFT OF THAT SET, OUTSIDE IT. The quote pins three controls
  relative to each other and says nothing about a fourth; putting Share left of
  Rename satisfies it exactly and leaves Delete furthest right, which is the
  part of the instruction with a safety reason behind it. It is behind
  `FOLDER_SHARING`, which is false until TASK-040 writes the dialog (§3d).

  Absent, not disabled (§3a):

    | View          | Rename | New Folder | Delete | Share |
    | A folder      | yes    | yes        | yes    | flag  |
    | Root          | no     | yes        | no     | no    |
    | All documents | no     | no         | no     | no    |
    | Deleted       | no     | no         | no     | no    |

  The root cannot be renamed or deleted because there is no row to rename or
  delete (§1). That is not a permission that might one day be granted, it is a
  thing that can never exist, and a control that can never become enabled is
  noise on every visit. The toolbar is right-aligned, so at the root its one
  button ends where Delete would have ended and the right edge does not move
  between views.
-->
<script lang="ts">
  import type { View } from './doclist-types'
  import { FOLDER_SHARING } from './doclist-types'

  let {
    view,
    busy = false,
    onrename,
    oncreate,
    ondelete,
    onshare,
  }: {
    view: View
    busy?: boolean
    onrename: () => void
    oncreate: () => void
    ondelete: () => void
    onshare?: () => void
  } = $props()

  const isFolder = $derived(view.kind === 'folder')
  const isRoot = $derived(view.kind === 'root')
</script>

{#if isFolder || isRoot}
  <div class="toolbar" role="group" aria-label="Folder actions">
    {#if FOLDER_SHARING && isFolder}
      <button type="button" class="tool" disabled={busy} onclick={() => onshare?.()}>Share…</button>
    {/if}
    {#if isFolder}
      <button type="button" class="tool" disabled={busy} onclick={onrename}>Rename</button>
    {/if}
    <button type="button" class="tool" disabled={busy} onclick={oncreate}>New Folder</button>
    {#if isFolder}
      <button type="button" class="tool tool-danger" disabled={busy} onclick={ondelete}>
        Delete
      </button>
    {/if}
  </div>
{/if}

<style>
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.25rem;
  }

  .tool {
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.375rem;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: var(--text-meta, 0.8125rem);
    padding: 0.3rem 0.6rem;
    cursor: pointer;
    white-space: nowrap;
  }

  .tool:hover:not(:disabled) {
    background: var(--color-plinth, #f1ece4);
  }

  .tool:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .tool-danger {
    color: var(--color-red-deep, #c01030);
  }
</style>
